import { useNavigate } from 'react-router-dom';
import { type MouseEvent, useMemo, useRef, useState } from 'react';
import type { LibraryItem, ProcessingStatus } from '@/shared/types';
import { useProcessing } from '@/app/providers/processing';
import { safePlay } from '@/shared/utils/media';
import { describeJobError } from '@/shared/api/jobErrors';
import { safeErrorMessage } from '@/shared/utils/errors';
import { formatDayMonth, formatDuration } from '@/shared/utils/format';
import type { Shelf } from '@/features/library/services/shelfService';
import {
  AlertIcon,
  Button,
  ConfirmDialog,
  HeartIcon,
  IconButton,
  Menu,
  MenuItem,
  MenuSeparator,
  MenuSubmenu,
  MoreIcon,
  PlayIcon,
  PlusIcon,
  ShelfIcon,
  ShareIcon,
  Tag,
  TrashIcon,
  cx,
} from '@/shared/ui';

const statusLabels: Record<ProcessingStatus, string> = {
  completed: 'Ready',
  processing: 'Rendering',
  queued: 'Queued',
  failed: 'Failed',
  cancelled: 'Cancelled',
  awaiting_confirmation: 'Awaiting confirmation',
};

const statusTones: Record<ProcessingStatus, 'green' | 'warn' | 'danger' | 'neutral'> = {
  completed: 'green',
  processing: 'warn',
  queued: 'neutral',
  failed: 'danger',
  cancelled: 'neutral',
  awaiting_confirmation: 'warn',
};

export type LibraryCardProps = {
  item: LibraryItem;
  /** Shelves this item sits on, already resolved by the page. */
  shelves?: Shelf[];
  /** Every shelf available to move into. */
  allShelves?: Shelf[];
  /** 0–1 watch position, drives the bar across the bottom of the thumbnail. */
  watched?: number;
  /** True when the item came from the browse feed rather than a generation. */
  saved?: boolean;
  onAddToShelf?: (shelfId: string) => void;
  onRemoveFromShelf?: (shelfId: string) => void;
  onCreateShelf?: () => void;
  onToggleFavorite?: () => void;
  onShare?: () => void;
  /**
   * Delete the video permanently, after the card has confirmed it.
   *
   * May reject, and the card depends on that: a failure has to keep the dialog
   * open and say so rather than closing on a deletion that did not happen. The
   * page removes the item from the grid only once this resolves.
   */
  onDelete?: () => void | Promise<void>;
};

export function LibraryCard({
  item,
  shelves = [],
  allShelves = [],
  watched,
  saved = false,
  onAddToShelf,
  onRemoveFromShelf,
  onCreateShelf,
  onToggleFavorite,
  onShare,
  onDelete,
}: LibraryCardProps) {
  const navigate = useNavigate();
  const { resumeJob, cancelJob } = useProcessing();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | undefined>();
  const [resuming, setResuming] = useState(false);
  /* The reason, not a flag. A refused retry is nearly always a billing answer
     with a number in it, and the page this card replaced printed that answer on
     the job's row; "Try again" invites the user to do exactly the thing that
     will fail again for the same reason. Generic copy remains the fallback for
     a failure with nothing authored to say. */
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  const status = item.status ?? 'completed';
  const isProcessing = status === 'processing' || status === 'queued';
  /* Length comes from the real video only: the loaded <video> element's duration
     wins, then the backend's. `formatDuration` returns null while unknown — a
     queued job has no duration yet, and a badge there would be a guess presented
     as fact. */
  const durationText = formatDuration(videoDuration ?? item.durationSeconds);
  const shelfIds = useMemo(() => new Set(shelves.map((shelf) => shelf.id)), [shelves]);

  const open = () => navigate(`/videos/${item.id}`);

  // The card is a link target, so every control inside it has to stop the click
  // from bubbling up and navigating away.
  const stop = (event: MouseEvent) => event.stopPropagation();

  const handleResume = async (event: MouseEvent) => {
    event.stopPropagation();
    if (resuming) return;
    setResuming(true);
    setResumeError(null);
    try {
      await resumeJob(item.id);
    } catch (error) {
      // Same translation the content page's Retry uses, so the two cannot drift.
      setResumeError(describeJobError(error).message);
    } finally {
      setResuming(false);
    }
  };

  const handleCancel = async () => {
    if (cancelling) return;
    setConfirmingCancel(false);
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelJob(item.id);
    } catch (error) {
      setCancelError(safeErrorMessage(error, 'Couldn’t cancel. Try again.'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    /* The dialog is a sibling of the card, not a child. React routes synthetic
       events through the component tree rather than the DOM tree, so a portal
       rendered inside this <article> would bubble its clicks into `open` below
       and navigate to the video the moment you pressed Cancel. */
    <>
      <article
        className="lib-card"
        role="button"
        tabIndex={0}
        aria-label={item.title}
        onClick={open}
        /* The `stop` guards below keep mouse clicks on the inner controls from
           bubbling up here and navigating. Key events need the same boundary and
           did not have one, which made every control on the card keyboard-dead:
           Enter on Cancel bubbled to this handler, `preventDefault()` cancelled
           the keypress the browser would have turned into that button's click,
           and the card navigated instead. Same for the `···` menu, whose own
           Enter handler was being clobbered. Only act when the card itself is
           the target. */
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        }}
        onMouseEnter={() => safePlay(videoRef.current)}
        onMouseLeave={() => {
          const el = videoRef.current;
          if (!el) return;
          el.pause();
          el.currentTime = 0.1;
        }}
      >
        <div className="lib-card__thumb">
          {item.videoUrl ? (
            <video
              ref={videoRef}
              src={`${item.videoUrl}#t=0.1`}
              preload="metadata"
              muted
              playsInline
              loop
              onLoadedMetadata={(event) => {
                const d = event.currentTarget.duration;
                if (Number.isFinite(d) && d > 0) setVideoDuration(Math.round(d));
              }}
            />
          ) : item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" loading="lazy" />
          ) : (
            <div className="lib-card__placeholder" aria-hidden />
          )}

          {status !== 'completed' ? (
            <span className={cx('lib-card__status', `lib-card__status--${statusTones[status]}`)}>
              <span className="lib-card__status-dot" aria-hidden />
              {statusLabels[status]}
            </span>
          ) : null}

          {status === 'completed' ? (
            <span className="lib-card__play" aria-hidden>
              <PlayIcon size={18} />
            </span>
          ) : null}

          {durationText ? <span className="lib-card__duration">{durationText}</span> : null}

          {/* Continue-watching bar. Sits over the thumbnail's bottom edge, which is
              why it is not part of the body. */}
          {watched !== undefined && watched > 0 ? (
            <span
              className="lib-card__watched"
              role="progressbar"
              aria-label={`${Math.round(watched * 100)}% watched`}
              aria-valuenow={Math.round(watched * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span className="lib-card__watched-fill" style={{ width: `${watched * 100}%` }} />
            </span>
          ) : null}
        </div>

        <div className="lib-card__body">
          <div className="lib-card__head">
            <h3 className="lib-card__title">{item.title}</h3>

            <div className="lib-card__actions" onClick={stop} role="presentation">
              <Menu
                label={`${item.title} actions`}
                trigger={
                  <IconButton label={`${item.title} actions`} size="sm" variant="ghost">
                    <MoreIcon size={16} />
                  </IconButton>
                }
              >
                {allShelves.length > 0 ? (
                  <MenuSubmenu label="Move to shelf" icon={<ShelfIcon size={16} />}>
                    {allShelves
                      .filter((shelf) => !shelf.builtin)
                      .map((shelf) => (
                        <MenuItem
                          key={shelf.id}
                          onSelect={() =>
                            shelfIds.has(shelf.id)
                              ? onRemoveFromShelf?.(shelf.id)
                              : onAddToShelf?.(shelf.id)
                          }
                          checked={shelfIds.has(shelf.id)}
                        >
                          {shelf.name}
                        </MenuItem>
                      ))}
                    <MenuSeparator />
                    <MenuItem icon={<PlusIcon size={16} />} onSelect={() => onCreateShelf?.()}>
                      Start a new shelf
                    </MenuItem>
                  </MenuSubmenu>
                ) : null}

                <MenuItem icon={<HeartIcon size={16} />} onSelect={() => onToggleFavorite?.()}>
                  Favorite
                </MenuItem>
                {onShare ? (
                  <MenuItem icon={<ShareIcon size={16} />} onSelect={() => onShare()}>
                    Share
                  </MenuItem>
                ) : null}
                {/* Carries a request for the feedback form. Without the state
                    this was `navigate('/videos/:id')` — the same thing clicking
                    the card does — so the item read as "Report an issue" and
                    behaved as "Open", leaving the user to find the form. */}
                <MenuItem
                  icon={<AlertIcon size={16} />}
                  onSelect={() => navigate(`/videos/${item.id}`, { state: { focus: 'feedback' } })}
                >
                  Report an issue
                </MenuItem>
                {onDelete ? (
                  <>
                    <MenuSeparator />
                    {/* Opens the dialog rather than deleting — this is the one
                        irreversible action on the card, and it used to be a
                        single click away from "Report an issue". */}
                    <MenuItem
                      icon={<TrashIcon size={16} />}
                      tone="danger"
                      disabled={deleting}
                      onSelect={() => {
                        setDeleteError(false);
                        setConfirmingDelete(true);
                      }}
                    >
                      {deleting ? 'Deleting…' : 'Delete permanently'}
                    </MenuItem>
                  </>
                ) : null}
              </Menu>
            </div>
          </div>

          <div className="lib-card__meta">
            {shelves.length > 0 ? (
              <Tag color={shelves[0].color}>{shelves[0].name}</Tag>
            ) : item.tags?.[0] ? (
              <Tag color="gray">{item.tags[0]}</Tag>
            ) : null}
            {/* The label follows the date, which is the part that is not
                negotiable: a saved item's `createdAt` is its `savedAt` — when
                *you* added it — while a generated one's is when it was made.
                This read the two backwards, so every card stated the opposite of
                the fact beside it. The Figma annotation maps to the variants by
                x-position and three independent readings all took it this way;
                see the open question in docs/ui-inventory.md. */}
            <span className="lib-card__date">
              {saved ? 'Added' : 'Created'} {formatDayMonth(item.createdAt)}
            </span>
          </div>

          {status === 'failed' ? (
            <div className="lib-card__row" onClick={stop} role="presentation">
              <Button size="sm" onClick={handleResume} disabled={resuming}>
                {resuming ? 'Resuming…' : 'Retry generation'}
              </Button>
              {resumeError ? (
                <span role="alert" className="lib-card__error">
                  {resumeError}
                </span>
              ) : null}
            </div>
          ) : null}

          {isProcessing ? (
            <div className="lib-card__row" onClick={stop} role="presentation">
              <Button size="sm" onClick={() => setConfirmingCancel(true)} disabled={cancelling}>
                {cancelling ? 'Cancelling…' : 'Cancel'}
              </Button>
              {cancelError ? (
                <span role="alert" className="lib-card__error">
                  {cancelError}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </article>

      {/* "Cancel generation" / "Keep generating", not Confirm / Cancel — a
          confirmation dialog *about* cancelling cannot use the word Cancel for
          its dismiss button without asking the user to guess. */}
      <ConfirmDialog
        open={confirmingCancel}
        title="Cancel this generation?"
        description="The video won't be created, and this cannot be undone."
        confirmLabel="Cancel generation"
        cancelLabel="Keep generating"
        onConfirm={() => void handleCancel()}
        onCancel={() => setConfirmingCancel(false)}
      />

      {/* Permanent deletion, from #67. Same shape as the cancel dialog above,
          and the same naming rule: the dismiss button says "Keep video" rather
          than "Cancel", which in a dialog about deleting a *generation* would
          be asking the user to guess which cancel is meant.

          The dialog stays open on failure. Closing it would leave the card in
          the grid with no explanation, which reads as the delete having worked
          and the page being stale. */}
      <ConfirmDialog
        open={confirmingDelete}
        title="Delete this video permanently?"
        description={
          deleteError
            ? 'That didn’t work — the video is still here. Check your connection and try again.'
            : 'The video and its file are removed for good. This cannot be undone.'
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete permanently'}
        cancelLabel="Keep video"
        onConfirm={() => {
          if (deleting) return;
          setDeleting(true);
          setDeleteError(false);
          void Promise.resolve(onDelete?.())
            .then(() => setConfirmingDelete(false))
            .catch(() => setDeleteError(true))
            .finally(() => setDeleting(false));
        }}
        onCancel={() => {
          if (deleting) return;
          setDeleteError(false);
          setConfirmingDelete(false);
        }}
      />
    </>
  );
}
